import { Component, ViewChild } from '@angular/core';
import { CommissionsService } from './commissions.service';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from 'src/app/shared/services/auth.service';
import { TableTransactionsCommissionsComponent } from '../table-transactions-commissions/table-transactions-commissions.component';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-commissions',
  templateUrl: './commissions.component.html',
  styleUrls: ['./commissions.component.scss'],
})
export class CommissionsComponent {
  totalDeposito: number = 0;
  qtDeposito: number = 0;
  totalSaque: number = 0;
  qtSaque: number = 0;
  totalComissao: number = 0;

  comissao = new FormGroup({
    tipo: new FormControl<string>(''),
    status: new FormControl<string>(''),
    tipoBusca: new FormControl<string>(''),
    origem: new FormControl<string>(''),
    aplicacao: new FormControl<string>(''),
    responsavel: new FormControl<string>(''),
    initialDate: new FormControl<Date | null>(
      null,
      { validators: [Validators.required], updateOn: 'blur' }
    ),
    endDate: new FormControl<Date | null>(
      null,
      { validators: [Validators.required], updateOn: 'blur' }
    ),
  });

  public isFilterColapsed = false;
  public isSubmitted = false;
  public isLoaded: boolean = false;
  public transactions: any[] = [];

  private accountTimezoneOffsetMinutes: number | null = null;
  private originCurrency: string = 'BRL';

  @ViewChild(TableTransactionsCommissionsComponent) table!: TableTransactionsCommissionsComponent;

  constructor(
    private commissionService: CommissionsService,
    private authService: AuthService,
    private toastr: ToastrService
  ) {
    this.comissao.controls.status.setValue('REALIZADO');
    this.comissao.controls.tipoBusca.setValue('personName');
  }

  private parseTimezoneOffset(tz: string | null | undefined): number | null {
    if (!tz) return null;
    const m = /^([+-]?)(\d{1,2}):(\d{2})$/.exec(tz.trim());
    if (!m) return null;
    const sign = m[1] === '-' ? 1 : -1;
    const hours = Number(m[2]);
    const minutes = Number(m[3]);
    return sign * (hours * 60 + minutes);
  }

  private nowInAccountTz(): Date {
    const now = new Date();
    if (this.accountTimezoneOffsetMinutes == null) {
      return now;
    }
    const browserOffset = now.getTimezoneOffset();
    const diffMinutes = this.accountTimezoneOffsetMinutes - browserOffset;
    return new Date(now.getTime() - diffMinutes * 60000);
  }

  private buildDefaultRange(hasFlag: boolean): { start: Date; end: Date } {
    const origin = (this.originCurrency || 'BRL').toUpperCase();
    const nowRef = origin === 'BRL' ? new Date() : this.nowInAccountTz();

    const end = new Date(nowRef);
    const start = hasFlag
      ? new Date(nowRef.getTime() - 15 * 60 * 1000)
      : (() => {
          const d = new Date(nowRef);
          d.setHours(0, 0, 0, 0);
          return d;
        })();

    return { start, end };
  }

  async ngOnInit(): Promise<void> {
    try {
      const account = await firstValueFrom(this.authService.getAccount());
      const hasFlag = !!account?.featureFlagNewParam;

      this.originCurrency =
        ((account as any)?.originCurrency ||
          this.authService.getOriginCurrency?.() ||
          'BRL').toUpperCase();

      this.accountTimezoneOffsetMinutes = this.parseTimezoneOffset(
        (account as any)?.timezone
      );

      const { start, end } = this.buildDefaultRange(hasFlag);

      this.comissao.controls.initialDate.setValue(start);
      this.comissao.controls.endDate.setValue(end);
    } catch {
      const now = new Date();
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      this.comissao.controls.initialDate.setValue(start);
      this.comissao.controls.endDate.setValue(now);
    }
  }

  onTransactionsLoaded(lista: any[]): void {
    this.transactions = lista ?? [];

    const sum = (prop: keyof typeof lista[0]) =>
      this.transactions.reduce(
        (tot, t) => tot + (Number(t[prop]) || 0),
        0
      );

    this.totalDeposito = sum('valueCashIn');
    this.qtDeposito = sum('qtdeCahsIn');
    this.totalSaque = sum('valueCashOut');
    this.qtSaque = sum('qtdeCahsOut');
    this.totalComissao = sum('commission');

    this.isLoaded = true;
  }

  private toDate(v: any): string {
    const d = this.asDate(v);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  private toNgb(v: any): NgbDateStruct {
    const d = this.asDate(v);
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  }

  private asDate(v: any): Date {
    return v?.toDate ? v.toDate() : (v as Date);
  }

  private parseDate(s: string): Date | null {
    if (!s) return null;
    const m = s.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return null;
    const d = new Date(+m[3], +m[2] - 1, +m[1], 0, 0, 0, 0);
    return isNaN(d.getTime()) ? null : d;
  }

  parseDateInput(ctrlName: 'initialDate' | 'endDate', raw: string) {
    const ctrl = this.comissao.controls[ctrlName];
    const v = ctrl.value as any;
    if (v instanceof Date && !isNaN(v.getTime())) return;

    const parsed = this.parseDate(raw);
    if (parsed) {
      ctrl.setValue(parsed, { emitEvent: false });
      ctrl.setErrors(null);
    } else if (raw?.trim()) {
      ctrl.setErrors({ matDatepickerParse: { text: raw } });
    } else {
      ctrl.setValue(null, { emitEvent: false });
      ctrl.setErrors({ required: true });
    }
  }

  private fixDatesFromUI(startRaw?: string, endRaw?: string) {
    if (startRaw !== undefined) this.parseDateInput('initialDate', startRaw);
    if (endRaw !== undefined) this.parseDateInput('endDate', endRaw);
  }

  buscar() {
    const startInput = (document.querySelector('#inputInitialDate input') as HTMLInputElement)?.value;
    const endInput = (document.querySelector('#inputFinalDate input') as HTMLInputElement)?.value;

    this.fixDatesFromUI(startInput, endInput);

    this.comissao.markAllAsTouched();
    this.comissao.updateValueAndValidity({ emitEvent: false });

    const start = this.comissao.controls.initialDate.value as Date | null;
    const end = this.comissao.controls.endDate.value as Date | null;

    if (!start || !end) {
      return;
    }

    if (start > end) {
      this.toastr.error('Data inicial deve ser menor ou igual a data final!', 'Erro', {
        timeOut: 3000,
        positionClass: 'toast-top-right',
      });
      this.isLoaded = true;
      return;
    }

    if (this.comissao.invalid) {
      this.isLoaded = true;
      return;
    }

    this.isLoaded = false;

    const startStr = this.toDate(start);
    const endStr = this.toDate(end);

    this.table.page = 1;
    this.table.dataInitial = this.toNgb(start);
    this.table.dataFinal = this.toNgb(end);

    this.table.startDateIso = startStr;
    this.table.endDateIso = endStr;

    this.table.personName = this.comissao.controls.responsavel.value || '';
    this.table.getTransactions();

    this.isSubmitted = false;
  }

  async paste(): Promise<void> {
    try {
      const text = await navigator.clipboard.readText();
      this.comissao.controls.responsavel.setValue(text.trim());
    } catch (err) {
      console.error('Erro ao colar o código:', err);
    }
  }

  filterColapse() {
    this.isFilterColapsed = !this.isFilterColapsed;
  }

  downloadFile() {
    const start = this.comissao.controls.initialDate.value!;
    const end = this.comissao.controls.endDate.value!;

    this.commissionService
      .getCsvFile(
        start,
        end,
        this.comissao.controls.tipo.value!,
        this.comissao.controls.status.value!
      )
      .subscribe((value) => {
        const csvContent = decodeURIComponent(escape(atob(value)));
        const a = document.createElement('a');
        const blob = new Blob([csvContent], {
          type: 'text/csv;charset=utf-8;',
        });
        const url = window.URL.createObjectURL(blob);
        const company = this.authService.getCompany();
        a.href = url;
        a.download = `${company}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      });
  }
}
